import RateReviewOutlined from "@mui/icons-material/RateReviewOutlined";
import { Button } from "@mui/material";
import { useCallback } from "react";

import { applyShowFilterToSearch } from "./manifestShowFilters";
import { useShowFilterCounts } from "./useShowFilterCounts";

/**
 * How many manifests are waiting on a review, said on the page rather than
 * inside a dropdown.
 *
 * The count already existed as an option label in the Show picker, which meant a
 * reviewer had to suspect there was work and go looking for it before the page
 * would admit it. Now that customers review their own manifest documents, "is
 * there anything to do here" is the first question the screen should answer.
 *
 * Renders nothing at zero: a page with no outstanding review should look like a
 * page with nothing to do, and a standing "0 need review" is the kind of chrome
 * people stop reading.
 *
 * A button rather than a chip on purpose — the design system's filter-chip
 * selection pattern is still exploring, so this is deliberately not built to it.
 */
export function NeedsReviewPrompt({
  defaultSearch,
  omnisearch,
  onApply,
}: {
  /** The page scope the counts are taken against (tenant, and the year picker). */
  defaultSearch: string;
  /** The active omnisearch string, which applying the preset rewrites. */
  omnisearch: string;
  onApply: (nextOmnisearch: string) => void;
}) {
  const { countByFilterId } = useShowFilterCounts(defaultSearch);
  const count = countByFilterId.get("needsDocumentReview") ?? 0;

  const handleClick = useCallback(() => {
    onApply(applyShowFilterToSearch(omnisearch, "needsDocumentReview"));
  }, [omnisearch, onApply]);

  if (count === 0) return null;

  return (
    <Button
      size="small"
      color="primary"
      startIcon={<RateReviewOutlined fontSize="small" />}
      onClick={handleClick}
    >
      {count === 1 ? "1 manifest needs review" : `${count} manifests need review`}
    </Button>
  );
}
